import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Copy, MessageCircle, Phone, RefreshCw, CheckCircle, XCircle, Inbox } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../lib/store';

/**
 * Admin → WhatsApp tab.
 * Shows DoubleTick configuration, the webhook URL to paste in DoubleTick
 * dashboard, the staff whitelist (only staff with a `phone` set can use the
 * WhatsApp bot), recent inbound events, and active sessions.
 */
export default function WhatsAppAdminPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStatus = async () => {
    setLoading(true);
    try {
      const res = await api.getWhatsappStatus();
      setData(res.data);
    } catch (err) {
      toast.error('Failed to load WhatsApp status');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchStatus(); }, []);

  const copyUrl = (url) => {
    navigator.clipboard.writeText(url);
    toast.success('Webhook URL copied');
  };

  if (loading) {
    return <div className="text-sm text-muted-foreground py-8 text-center">Loading…</div>;
  }
  if (!data) return null;

  return (
    <div className="space-y-4" data-testid="admin-whatsapp-panel">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <MessageCircle className="w-5 h-5 text-[#25D366]" />
          <h2 className="text-base font-bold text-[#1A1C16]">WhatsApp Quotation Agent</h2>
          <Badge variant="outline" className="text-[10px] bg-amber-50 border-amber-300 text-amber-800">BETA</Badge>
        </div>
        <Button size="sm" variant="outline" onClick={fetchStatus} data-testid="whatsapp-refresh-btn">
          <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
        </Button>
      </div>

      {/* Configuration card */}
      <Card className="border-[#E2E6D9]">
        <CardContent className="pt-5 space-y-3">
          <div className="flex items-center gap-2">
            {data.configured ? (
              <>
                <CheckCircle className="w-4 h-4 text-green-600" />
                <span className="text-sm font-semibold text-green-700">Configured</span>
              </>
            ) : (
              <>
                <XCircle className="w-4 h-4 text-red-600" />
                <span className="text-sm font-semibold text-red-700">Not configured — check backend .env</span>
              </>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <div>
              <p className="text-xs text-[#5C6150] uppercase tracking-wide">Business Number</p>
              <p className="font-mono text-[#1A1C16]" data-testid="whatsapp-business-number">+{data.waba_number}</p>
            </div>
            <div>
              <p className="text-xs text-[#5C6150] uppercase tracking-wide">DoubleTick Base URL</p>
              <p className="font-mono text-[#1A1C16] text-xs break-all">{data.base_url}</p>
            </div>
          </div>

          {/* Webhook URL with copy */}
          {data.webhook_url && (
            <div className="mt-2 p-3 rounded-md border border-[#E2E6D9] bg-[#F9FAF7]">
              <p className="text-xs text-[#5C6150] uppercase tracking-wide mb-1">Webhook URL — paste this in DoubleTick → Settings → Webhooks</p>
              <div className="flex items-center gap-2">
                <code className="flex-1 text-xs font-mono bg-white border border-[#E2E6D9] rounded px-2 py-1 break-all" data-testid="whatsapp-webhook-url">
                  {data.webhook_url}
                </code>
                <Button size="sm" variant="outline" onClick={() => copyUrl(data.webhook_url)} data-testid="whatsapp-copy-webhook-btn">
                  <Copy className="w-3.5 h-3.5" />
                </Button>
              </div>
              <p className="text-[10px] text-[#8F9485] mt-1">
                Subscribe to <span className="font-semibold">Message Received</span> event in DoubleTick. Bot only responds to whitelisted senders below.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Allowed senders with per-user toggle */}
      <Card className="border-[#E2E6D9]">
        <CardContent className="pt-5">
          <div className="flex items-center gap-2 mb-3">
            <Phone className="w-4 h-4 text-[#556B2F]" />
            <h3 className="text-sm font-bold">
              Allowed Senders ({(data.whitelisted_users || []).filter(u => u.whatsapp_enabled).length} active / {data.whitelisted_users?.length || 0})
            </h3>
          </div>
          <p className="text-xs text-[#5C6150] mb-3">
            Toggle <span className="font-semibold">WhatsApp access</span> for each staff/admin user below. Only users
            with the toggle <span className="text-green-700 font-semibold">ON</span> can use the bot. Users without a phone
            number set don't appear here — edit them in the <span className="font-semibold">Staff</span> tab first.
          </p>
          {data.whitelisted_users?.length === 0 ? (
            <p className="text-sm text-[#8F9485] italic">No users with phone numbers configured yet. Add a phone number in the Staff tab.</p>
          ) : (
            <div className="border border-[#E2E6D9] rounded-md overflow-x-auto">
              <table className="w-full text-xs min-w-[640px]">
                <thead className="bg-[#F2F4EF] text-[#5C6150]">
                  <tr>
                    <th className="text-left py-1.5 px-3">Name</th>
                    <th className="text-left py-1.5 px-3">Phone</th>
                    <th className="text-left py-1.5 px-3">Role</th>
                    <th className="text-center py-1.5 px-3">Bot Access</th>
                    <th className="text-center py-1.5 px-3">Walk-in</th>
                    <th className="text-center py-1.5 px-3">Quotation</th>
                  </tr>
                </thead>
                <tbody>
                  {data.whitelisted_users.map((u, i) => (
                    <tr key={u.id || i} className="border-t border-[#E2E6D9]">
                      <td className="py-1.5 px-3 font-medium">{u.name}</td>
                      <td className="py-1.5 px-3 font-mono">{u.phone || '—'}</td>
                      <td className="py-1.5 px-3">
                        <Badge variant="outline" className="text-[10px]">{u.role}</Badge>
                      </td>
                      <td className="py-1.5 px-3 text-center">
                        <button
                          onClick={async () => {
                            try {
                              await api.toggleWhatsappUser(u.id, !u.whatsapp_enabled);
                              toast.success(`${u.name}: Bot ${!u.whatsapp_enabled ? 'enabled' : 'disabled'}`);
                              fetchStatus();
                            } catch (err) {
                              toast.error(err.response?.data?.detail || 'Failed to toggle');
                            }
                          }}
                          className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${u.whatsapp_enabled ? 'bg-[#25D366]' : 'bg-gray-300'}`}
                          data-testid={`whatsapp-toggle-${u.id}`}
                          title={u.whatsapp_enabled ? 'Click to disable' : 'Click to enable'}
                        >
                          <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${u.whatsapp_enabled ? 'translate-x-5' : 'translate-x-1'}`} />
                        </button>
                      </td>
                      <td className="py-1.5 px-3 text-center">
                        <button
                          disabled={!u.whatsapp_enabled}
                          onClick={async () => {
                            try {
                              await api.toggleWhatsappFeature(u.id, 'walkin', !u.whatsapp_walkin_enabled);
                              toast.success(`${u.name}: Walk-in ${!u.whatsapp_walkin_enabled ? 'ON' : 'OFF'}`);
                              fetchStatus();
                            } catch (err) {
                              toast.error(err.response?.data?.detail || 'Failed to toggle');
                            }
                          }}
                          className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${u.whatsapp_walkin_enabled ? 'bg-[#556B2F]' : 'bg-gray-300'}`}
                          data-testid={`walkin-toggle-${u.id}`}
                          title={!u.whatsapp_enabled ? 'Enable Bot Access first' : (u.whatsapp_walkin_enabled ? 'Disable walk-in flow' : 'Enable walk-in flow')}
                        >
                          <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${u.whatsapp_walkin_enabled ? 'translate-x-5' : 'translate-x-1'}`} />
                        </button>
                      </td>
                      <td className="py-1.5 px-3 text-center">
                        <button
                          disabled={!u.whatsapp_enabled}
                          onClick={async () => {
                            try {
                              await api.toggleWhatsappFeature(u.id, 'quotation', !u.whatsapp_quotation_enabled);
                              toast.success(`${u.name}: Quotation ${!u.whatsapp_quotation_enabled ? 'ON' : 'OFF'}`);
                              fetchStatus();
                            } catch (err) {
                              toast.error(err.response?.data?.detail || 'Failed to toggle');
                            }
                          }}
                          className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${u.whatsapp_quotation_enabled ? 'bg-[#DAA520]' : 'bg-gray-300'}`}
                          data-testid={`quotation-toggle-${u.id}`}
                          title={!u.whatsapp_enabled ? 'Enable Bot Access first' : (u.whatsapp_quotation_enabled ? 'Disable quotation flow' : 'Enable quotation flow')}
                        >
                          <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${u.whatsapp_quotation_enabled ? 'translate-x-5' : 'translate-x-1'}`} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="text-[10px] text-[#8F9485] px-3 py-2 bg-[#FAFBF5] border-t border-[#E2E6D9]">
                <span className="font-semibold">Walk-in</span> — salesperson sends voice/text to log a walk-in customer.
                {' '}<span className="font-semibold">Quotation</span> — salesperson sends voice/text and gets a PDF quotation back.
                {' '}Quotation flow is in trial mode — keep it OFF for staff until UAT passes.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recent sessions */}
      <Card className="border-[#E2E6D9]">
        <CardContent className="pt-5">
          <div className="flex items-center gap-2 mb-3">
            <Inbox className="w-4 h-4 text-[#556B2F]" />
            <h3 className="text-sm font-bold">Recent Sessions</h3>
          </div>
          {(!data.recent_sessions || data.recent_sessions.length === 0) ? (
            <p className="text-sm text-[#8F9485] italic">No WhatsApp conversations yet.</p>
          ) : (
            <div className="space-y-2">
              {data.recent_sessions.map((s, i) => (
                <div key={i} className="border border-[#E2E6D9] rounded-md p-2 text-xs flex items-center justify-between">
                  <div>
                    <p className="font-mono font-semibold">+{s.phone}</p>
                    <p className="text-[#5C6150]">State: <span className="font-semibold">{s.state}</span></p>
                  </div>
                  <p className="text-[#8F9485]">{new Date(s.updated_at).toLocaleString()}</p>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Setup instructions */}
      <Card className="border-[#E2E6D9] bg-[#FDF6E1]/40">
        <CardContent className="pt-5 space-y-2 text-xs">
          <p className="text-sm font-bold text-[#8B6914]">Setup Instructions</p>
          <ol className="list-decimal list-inside space-y-1 text-[#5C6150]">
            <li>Log into <a href="https://web.doubletick.io" target="_blank" rel="noreferrer" className="text-blue-600 underline">DoubleTick dashboard</a> → Settings → Webhooks.</li>
            <li>Click <span className="font-semibold">+ New Webhook</span>, paste the URL above, and subscribe to <span className="font-semibold">Message Received</span>.</li>
            <li>On the Staff tab, set a WhatsApp phone number for each user who should be allowed to use the bot.</li>
            <li>Staff sends a message like <span className="font-mono bg-white px-1 rounded">"Name: Rohit Sharma, 9876543210, Century 18MM 5 sheets, Sainik 6MM 3 sheets"</span> — bot replies with summary + Confirm button → on Confirm, sends back a PDF quotation.</li>
            <li>Voice notes work too (any language).</li>
          </ol>
        </CardContent>
      </Card>
    </div>
  );
}
